<template>
  <div class="operator-dashboard">
    <!-- Mostrar loading si no hay usuario -->
    <div v-if="!currentUser" class="d-flex justify-center align-center" style="height: 50vh;">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </div>
    
    <v-container v-else fluid>
      <!-- Header del operario -->
      <v-row class="mb-4">
        <v-col cols="12">
          <v-card class="operator-header" data-tour="operator-header">
            <v-card-text>
              <div class="d-flex flex-column flex-md-row align-start align-md-center ga-3">
                <div class="d-flex align-center">
                  <v-avatar color="primary" size="60" class="mr-4">
                    <v-icon size="30">mdi-account-hard-hat</v-icon>
                  </v-avatar>
                  <div class="operator-info">
                    <h2 class="text-h4 font-weight-bold text-white operator-name">{{ currentUser?.username || 'Usuario' }}</h2>
                    <p class="text-subtitle-1 text-white mb-0 opacity-90">Operario de Almacén</p>
                    <p class="text-caption text-white opacity-75 operator-email">{{ currentUser?.email || '' }}</p>
                  </div>
                </div>
                <v-spacer class="d-none d-md-flex"></v-spacer>
                <div class="text-center align-self-center" data-tour="tasks-summary">
                  <div class="text-h3 font-weight-bold text-white">{{ totalActiveTasksCount }}</div>
                  <div class="text-subtitle-2 text-white opacity-90">Tareas Activas</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Estadísticas del operario -->
      <v-row class="mb-6" data-tour="operator-stats">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="warning" size="40" class="mr-3">mdi-clock-outline</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ totalPendingCount }}</div>
                  <div class="text-subtitle-2 text-grey">Pendientes</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="info" size="40" class="mr-3">mdi-play-circle</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ totalInProgressCount }}</div>
                  <div class="text-subtitle-2 text-grey">En Progreso</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="success" size="40" class="mr-3">mdi-check-circle</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ totalCompletedCount }}</div>
                  <div class="text-subtitle-2 text-grey">Completadas</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="primary" size="40" class="mr-3">mdi-timer</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ totalEstimatedTime }}</div>
                  <div class="text-subtitle-2 text-grey">Min. Estimados</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Tabs para diferentes tipos de tareas -->
      <v-tabs v-model="activeTab" class="mb-4">
        <v-tab value="picking">
          <v-icon class="mr-2">mdi-package-variant-closed</v-icon>
          Picking ({{ myPickingLists.length }})
        </v-tab>
        <v-tab value="storage">
          <v-icon class="mr-2">mdi-warehouse</v-icon>
          Almacenamiento ({{ myStorageLists.length }})
        </v-tab>
      </v-tabs>

      <!-- Tab de Picking -->
      <v-window v-model="activeTab">
        <v-window-item value="picking">
          <v-card data-tour="picking-list">
            <v-card-title>
              <span class="text-h6">Mis Tareas de Picking</span>
              <v-spacer></v-spacer>
              <v-btn icon @click="loadMyPickingLists">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-card-title>

            <ResponsiveDataTable
              :headers="pickingHeaders"
              :items="myPickingLists"
              :loading="loadingPicking"
              class="picking-table picking-list-table"
              item-key="id"
              data-tour="picking-list-table"
            >
              <template v-slot:item.status="{ item }">
                <v-chip :color="getStatusColor(item.status)" size="small">
                  {{ getStatusText(item.status) }}
                </v-chip>
              </template>

              <template v-slot:item.priority="{ item }">
                <v-chip :color="getPriorityColor(item.priority)" size="small">
                  {{ getPriorityText(item.priority) }}
                </v-chip>
              </template>

              <template v-slot:item.estimated_time="{ item }">
                {{ item.estimated_time }} min
              </template>

              <template v-slot:item.assigned_at="{ item }">
                {{ formatDateTime(item.assigned_at) }}
              </template>

              <template v-slot:item.actions="{ item }">
                <div class="d-flex gap-2">
                  <v-btn
                    v-if="item.status === 'assigned'"
                    color="primary"
                    size="small"
                    @click="startPicking(item)"
                    prepend-icon="mdi-play"
                    class="start-picking-btn"
                    data-tour="start-picking"
                  >
                    Iniciar
                  </v-btn>
                  
                  <v-btn
                    v-if="item.status === 'in_progress'"
                    color="success"
                    size="small"
                    @click="viewPickingDetails(item)"
                    prepend-icon="mdi-eye"
                    class="continue-picking-btn"
                    data-tour="continue-picking"
                  >
                    Continuar
                  </v-btn>
                  
                  <v-btn
                    v-if="item.status === 'completed'"
                    color="info"
                    size="small"
                    @click="viewPickingDetails(item)"
                    prepend-icon="mdi-eye"
                    class="view-picking-btn"
                    data-tour="view-picking"
                  >
                    Ver
                  </v-btn>
                </div>
              </template>
            </ResponsiveDataTable>
          </v-card>
        </v-window-item>

        <!-- Tab de Almacenamiento -->
        <v-window-item value="storage">
          <v-card data-tour="storage-list">
            <v-card-title>
              <span class="text-h6">Mis Tareas de Almacenamiento</span>
              <v-spacer></v-spacer>
              <v-btn icon @click="loadMyStorageLists">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-card-title>

            <ResponsiveDataTable
              :headers="storageHeaders"
              :items="myStorageLists"
              :loading="loadingStorage"
              class="storage-table storage-list-table"
              item-key="id"
              data-tour="storage-list-table"
            >
              <template v-slot:item.status="{ item }">
                <v-chip :color="getStorageStatusColor(item.status)" size="small">
                  {{ getStorageStatusText(item.status) }}
                </v-chip>
              </template>

              <template v-slot:item.storage_strategy="{ item }">
                <v-chip color="info" size="small">
                  {{ getStorageStrategyText(item.storage_strategy) }}
                </v-chip>
              </template>

              <template v-slot:item.progress="{ item }">
                <div class="d-flex align-center">
                  <v-progress-linear
                    :model-value="getStorageProgress(item)"
                    color="success"
                    height="8"
                    class="mr-2"
                    style="min-width: 60px;"
                  ></v-progress-linear>
                  <span class="text-caption">{{ item.completed_items }}/{{ item.total_items }}</span>
                </div>
              </template>

              <template v-slot:item.estimated_time="{ item }">
                {{ item.estimated_time }} min
              </template>

              <template v-slot:item.assigned_at="{ item }">
                {{ formatDateTime(item.assigned_at) }}
              </template>

              <template v-slot:item.actions="{ item }">
                <div class="d-flex gap-2">
                  <v-btn
                    v-if="item.status === 'assigned'"
                    color="primary"
                    size="small"
                    @click="startStorage(item)"
                    prepend-icon="mdi-play"
                    class="start-storage-btn"
                  >
                    Iniciar
                  </v-btn>
                  
                  <v-btn
                    v-if="item.status === 'in_progress'"
                    color="success"
                    size="small"
                    @click="continueStorage(item)"
                    prepend-icon="mdi-warehouse"
                    class="continue-storage-btn"
                  >
                    Continuar
                  </v-btn>
                  
                  <v-btn
                    v-if="item.status === 'completed'"
                    color="info"
                    size="small"
                    @click="viewStorageDetails(item)"
                    prepend-icon="mdi-eye"
                    class="view-storage-btn"
                  >
                    Ver
                  </v-btn>
                </div>
              </template>
            </ResponsiveDataTable>
          </v-card>
        </v-window-item>
      </v-window>
    </v-container>

    <!-- Dialog de detalles de picking -->
    <v-dialog v-model="showPickingDialog" max-width="800px" persistent>
      <v-card v-if="selectedPickingList" data-tour="picking-details">
        <v-card-title>
          <span class="text-h5">{{ selectedPickingList.list_number }}</span>
          <v-spacer></v-spacer>
          <v-chip :color="getStatusColor(selectedPickingList.status)">
            {{ getStatusText(selectedPickingList.status) }}
          </v-chip>
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <strong>Orden:</strong> {{ selectedPickingList.order_number }}
              </div>
              <div class="mb-3">
                <strong>Tiempo Estimado:</strong> {{ selectedPickingList.estimated_time }} minutos
              </div>
              <div class="mb-3">
                <strong>Asignado:</strong> {{ formatDateTime(selectedPickingList.assigned_at) }}
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3" v-if="selectedPickingList.started_at">
                <strong>Iniciado:</strong> {{ formatDateTime(selectedPickingList.started_at) }}
              </div>
              <div class="mb-3" v-if="selectedPickingList.completed_at">
                <strong>Completado:</strong> {{ formatDateTime(selectedPickingList.completed_at) }}
              </div>
              <div class="mb-3">
                <strong>Prioridad:</strong> 
                <v-chip :color="getPriorityColor(selectedPickingList.priority)" size="small">
                  {{ getPriorityText(selectedPickingList.priority) }}
                </v-chip>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <h3 class="mb-3">Items a Recolectar</h3>
          
          <ResponsiveDataTable
            :headers="itemHeaders"
            :items="selectedPickingList.picking_items || []"
            class="items-table"
            item-key="id"
            hide-default-footer
          >
            <template v-slot:item.status="{ item }">
              <v-chip :color="getItemStatusColor(item.status)" size="small">
                {{ getItemStatusText(item.status) }}
              </v-chip>
            </template>

            <template v-slot:item.quantity="{ item }">
              <div v-if="selectedPickingList.order?.piece_by_piece_enabled">
                <!-- Mostrar progreso de escaneos para piece-by-piece -->
                <div class="text-body-2">
                  <span :class="item.quantity_scanned >= item.quantity_required ? 'text-success font-weight-bold' : ''">
                    {{ item.quantity_scanned || 0 }} / {{ item.quantity_required }}
                  </span>
                  <span class="text-caption text-grey ml-1">escaneados</span>
                </div>
                <div class="text-caption text-grey">
                  {{ item.quantity_picked || 0 }} / {{ item.quantity_required }} recolectados
                </div>
              </div>
              <div v-else>
                <!-- Mostrar solo recolectados para picking tradicional -->
                {{ item.quantity_picked || 0 }} / {{ item.quantity_required }}
              </div>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn
                v-if="selectedPickingList.status === 'in_progress' && item.status === 'pending'"
                color="success"
                size="small"
                @click="pickItem(item)"
                prepend-icon="mdi-check"
              >
                Recolectar
              </v-btn>
              
              <!-- Botón de escaneo para picking piece-by-piece -->
              <v-btn
                v-if="selectedPickingList.status === 'in_progress' && 
                      item.status === 'pending' && 
                      selectedPickingList.order?.piece_by_piece_enabled &&
                      (item.quantity_scanned || 0) < item.quantity_required"
                color="primary"
                size="small"
                @click="startScanForItem(item)"
                prepend-icon="mdi-barcode-scan"
                class="mr-2"
              >
                Escanear
              </v-btn>
              
              <!-- Indicador de progreso de escaneo -->
              <v-chip
                v-if="selectedPickingList.order?.piece_by_piece_enabled && 
                      (item.quantity_scanned || 0) > 0 && 
                      (item.quantity_scanned || 0) < item.quantity_required"
                color="warning"
                size="small"
                prepend-icon="mdi-progress-clock"
                class="mr-2"
              >
                {{ item.quantity_scanned }}/{{ item.quantity_required }}
              </v-chip>
              
              <!-- Indicador de escaneado completo -->
              <v-chip
                v-if="selectedPickingList.order?.piece_by_piece_enabled && 
                      (item.quantity_scanned || 0) >= item.quantity_required"
                color="success"
                size="small"
                prepend-icon="mdi-check-circle"
                class="mr-2"
              >
                Completo
              </v-chip>
            </template>
          </ResponsiveDataTable>
          
          <!-- Sección de Ruta Optimizada -->
          <v-divider class="my-4"></v-divider>
          
          <div class="d-flex justify-space-between align-center mb-3">
            <h3>Ruta Optimizada</h3>
            <div class="d-flex gap-2">
              <v-btn
                v-if="!routeSuggestions || !routeSuggestions.is_optimized"
                color="primary"
                size="small"
                @click="optimizeRoute"
                prepend-icon="mdi-map-marker-path"
                :loading="optimizingRoute"
              >
                Optimizar Ruta
              </v-btn>
              <v-btn
                v-if="routeSuggestions"
                color="info"
                size="small"
                @click="refreshRouteSuggestions"
                prepend-icon="mdi-refresh"
                :loading="loadingRoute"
              >
                Actualizar
              </v-btn>
            </div>
          </div>
          
          <!-- Información de la ruta -->
          <v-card v-if="routeSuggestions" class="mb-4" variant="outlined">
            <v-card-text>
              <v-row>
                <v-col cols="12" md="3">
                  <div class="text-center">
                    <div class="text-h6 font-weight-bold text-primary">{{ routeSuggestions.total_locations || 0 }}</div>
                    <div class="text-caption text-grey">Ubicaciones</div>
                  </div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-center">
                    <div class="text-h6 font-weight-bold text-success">{{ routeSuggestions.estimated_time || 0 }}</div>
                    <div class="text-caption text-grey">Min. Estimados</div>
                  </div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-center">
                    <div class="text-h6 font-weight-bold text-info">{{ routeSuggestions.strategy || 'N/A' }}</div>
                    <div class="text-caption text-grey">Estrategia</div>
                  </div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-center">
                    <v-chip :color="routeSuggestions.is_optimized ? 'success' : 'warning'" size="small">
                      {{ routeSuggestions.is_optimized ? 'Optimizada' : 'Sin Optimizar' }}
                    </v-chip>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          
          <!-- Lista de ubicaciones en orden optimizado -->
          <v-expansion-panels v-if="routeSuggestions && routeSuggestions.route" class="mb-4">
            <v-expansion-panel
              v-for="(location, index) in routeSuggestions.route"
              :key="location.location_id"
              :title="`${location.sequence}. ${location.location_code}`"
            >
              <template v-slot:text>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="mb-2">
                      <strong>Ubicación:</strong> {{ location.location_code }}
                    </div>
                    <div class="mb-2" v-if="location.zone_info && location.zone_info.zone_name">
                      <strong>Zona:</strong> 
                      <v-chip size="small" :color="getZoneWeightColor(location.zone_info.zone_weight)">
                        {{ location.zone_info.zone_name }} (Peso: {{ location.zone_info.zone_weight }})
                      </v-chip>
                    </div>
                    <div class="mb-2">
                      <strong>Coordenadas:</strong> 
                      Pasillo {{ location.aisle || '-' }}, 
                      Estante {{ location.rack || '-' }}, 
                      Nivel {{ location.level || '-' }}
                    </div>
                    <div class="mb-2" v-if="location.distance_from_previous > 0">
                      <strong>Distancia desde anterior:</strong> {{ location.distance_from_previous }} unidades
                    </div>
                    <div class="mb-2" v-if="location.estimated_time_minutes">
                      <strong>Tiempo estimado:</strong> {{ location.estimated_time_minutes }} min
                    </div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="mb-2">
                      <strong>Items a recolectar ({{ location.item_count }}):</strong>
                    </div>
                    <v-list density="compact">
                      <v-list-item
                        v-for="item in location.items"
                        :key="item.picking_item_id || item.product_id"
                        class="px-0"
                      >
                        <template v-slot:prepend>
                          <v-icon 
                            :color="getItemStatusColor(item.status)" 
                            size="small"
                          >
                            {{ item.status === 'picked' ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                          </v-icon>
                        </template>
                        <v-list-item-title class="text-body-2">
                          {{ item.product_name }} ({{ item.product_sku }})
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-caption">
                          Cantidad: {{ item.quantity }}
                        </v-list-item-subtitle>
                      </v-list-item>
                    </v-list>
                  </v-col>
                </v-row>
              </template>
            </v-expansion-panel>
          </v-expansion-panels>
          
          <!-- Mensaje si no hay ruta optimizada -->
          <v-alert
            v-if="!routeSuggestions"
            type="info"
            variant="outlined"
            class="mb-4"
          >
            <div class="d-flex align-center">
              <v-icon class="mr-2">mdi-information</v-icon>
              <div>
                <div class="font-weight-medium">Ruta no optimizada</div>
                <div class="text-caption">Haz clic en "Optimizar Ruta" para obtener la secuencia más eficiente de recolección.</div>
              </div>
            </div>
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="closePickingDialog">Cerrar</v-btn>
          
          <v-btn
            v-if="selectedPickingList.status === 'in_progress' && allItemsPicked"
            color="success"
            @click="completePicking"
            prepend-icon="mdi-check-all"
          >
            Completar Picking
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Escáner de Códigos de Barras -->
    <v-dialog v-model="showBarcodeScanner" max-width="500" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">Escanear Código de Barras</span>
          <v-spacer></v-spacer>
          <div v-if="currentScanningItem" class="d-flex flex-column align-end">
            <v-chip color="primary" size="small" class="mb-1">
              {{ currentScanningItem.product_name }}
            </v-chip>
            <v-chip 
              :color="(currentScanningItem.quantity_scanned || 0) >= currentScanningItem.quantity_required ? 'success' : 'warning'" 
              size="small"
            >
              {{ currentScanningItem.quantity_scanned || 0 }}/{{ currentScanningItem.quantity_required }} escaneados
            </v-chip>
          </div>
        </v-card-title>
        
        <v-card-text>
          <v-tabs v-model="scanTab" class="mb-4">
            <v-tab value="camera" :disabled="!cameraAvailable">
              <v-icon class="mr-2">mdi-camera</v-icon>
              Cámara
            </v-tab>
            <v-tab value="manual">
              <v-icon class="mr-2">mdi-keyboard</v-icon>
              Manual
            </v-tab>
          </v-tabs>
          
          <v-window v-model="scanTab">
            <!-- Tab de Cámara -->
            <v-window-item value="camera">
              <div class="text-center">
                <video ref="videoElement" class="barcode-scanner" autoplay></video>
                
                <div class="mt-3">
                  <p class="text-body-2 text-grey">
                    Apunte la cámara hacia el código de barras del producto
                  </p>
                  <p class="text-caption text-grey" v-if="currentScanningItem">
                    Producto esperado: {{ currentScanningItem.product_name }}
                  </p>
                  <p class="text-caption text-success font-weight-bold" v-if="currentScanningItem && currentScanningItem.product_barcode">
                    Código esperado: {{ currentScanningItem.product_barcode }}
                  </p>
                </div>
              </div>
            </v-window-item>
            
            <!-- Tab Manual -->
            <v-window-item value="manual">
              <div class="text-center">
                <v-text-field
                  v-model="manualBarcode"
                  label="Código de Barras"
                  placeholder="Ingrese el código de barras manualmente"
                  variant="outlined"
                  class="mb-3"
                  @keyup.enter="handleManualBarcodeScan"
                  autofocus
                >
                  <template v-slot:append>
                    <v-btn
                      color="primary"
                      @click="handleManualBarcodeScan"
                      :disabled="!manualBarcode"
                      size="small"
                    >
                      Validar
                    </v-btn>
                  </template>
                </v-text-field>
                
                <div class="mt-3">
                  <p class="text-body-2 text-grey">
                    Ingrese el código de barras del producto manualmente
                  </p>
                  <p class="text-caption text-grey" v-if="currentScanningItem">
                    Producto esperado: {{ currentScanningItem.product_name }}
                  </p>
                  <p class="text-caption text-success font-weight-bold" v-if="currentScanningItem && currentScanningItem.product_barcode">
                    Código esperado: {{ currentScanningItem.product_barcode }}
                  </p>
                </div>
              </div>
            </v-window-item>
          </v-window>
          
          <v-alert
            v-if="scanError"
            type="error"
            class="mt-3"
            dismissible
            @click:close="scanError = ''"
          >
            {{ scanError }}
          </v-alert>
          
          <v-alert
            v-if="scanSuccess"
            type="success"
            class="mt-3"
            dismissible
            @click:close="scanSuccess = ''"
          >
            {{ scanSuccess }}
          </v-alert>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="stopScanner" color="grey">
            Cancelar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para notificaciones -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import api from '@/api'
import { stripAIM } from '@/utils/gs1Parse'
import { mapGetters } from 'vuex'
import { BrowserMultiFormatReader } from '@zxing/library'
import ResponsiveDataTable from '@/components/ResponsiveDataTable.vue'

export default {
  name: 'OperatorDashboard',
  components: {
    ResponsiveDataTable
  },
  data() {
    return {
      loading: false,
      myPickingLists: [],
      selectedPickingList: null,
      showPickingDialog: false,
      
      // Headers de tabla principal
      pickingHeaders: [
        { title: 'Lista', key: 'list_number', sortable: true },
        { title: 'Orden', key: 'order_number', sortable: true },
        { title: 'Estado', key: 'status', sortable: true },
        { title: 'Prioridad', key: 'priority', sortable: true },
        { title: 'Tiempo Est.', key: 'estimated_time', sortable: true },
        { title: 'Asignado', key: 'assigned_at', sortable: true },
        { title: 'Acciones', key: 'actions', sortable: false }
      ],
      
      // Headers de tabla de almacenamiento
      storageHeaders: [
        { title: 'Lista', key: 'list_number', sortable: true },
        { title: 'Estado', key: 'status', sortable: true },
        { title: 'Estrategia', key: 'storage_strategy', sortable: true },
        { title: 'Progreso', key: 'progress', sortable: true },
        { title: 'Tiempo Est.', key: 'estimated_time', sortable: true },
        { title: 'Asignado', key: 'assigned_at', sortable: true },
        { title: 'Acciones', key: 'actions', sortable: false }
      ],
      
      // Headers de tabla de items
      itemHeaders: [
        { title: 'Secuencia', key: 'sequence', sortable: true },
        { title: 'Producto', key: 'product_name', sortable: false },
        { title: 'SKU', key: 'product_sku', sortable: false },
        { title: 'Ubicación', key: 'location_code', sortable: false },
        { title: 'Cantidad', key: 'quantity', sortable: false },
        { title: 'Estado', key: 'status', sortable: false },
        { title: 'Acciones', key: 'actions', sortable: false }
      ],
      
      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },
      
      routeSuggestions: null,
      optimizingRoute: false,
      loadingRoute: false,
      showBarcodeScanner: false,
      currentScanningItem: null,
      scanError: '',
      scanSuccess: '',
      browserMultiFormatReader: null,
      scanTab: 'camera',
      manualBarcode: '',
      cameraAvailable: true,
      
      // Interval para actualizar progreso de escaneo
      progressUpdateInterval: null,
      
      // Tabs y datos de almacenamiento
      activeTab: 'picking',
      myStorageLists: [],
      loadingStorage: false,
      loadingPicking: false
    }
  },
  
  computed: {
    ...mapGetters('auth', ['currentUser']),
    
    pendingCount() {
      return this.myPickingLists.filter(list => list.status === 'assigned').length
    },
    inProgressCount() {
      return this.myPickingLists.filter(list => list.status === 'in_progress').length
    },
    completedCount() {
      return this.myPickingLists.filter(list => list.status === 'completed').length
    },
    activeTasksCount() {
      // Solo contar tareas asignadas y en progreso (no completadas)
      return this.myPickingLists.filter(list => 
        ['assigned', 'in_progress'].includes(list.status)
      ).length
    },
    pickingEstimatedTime() {
      return this.myPickingLists
        .filter(list => ['assigned', 'in_progress'].includes(list.status))
        .reduce((total, list) => total + (list.estimated_time || 0), 0)
    },
    allItemsPicked() {
      if (!this.selectedPickingList?.picking_items) return false
      return this.selectedPickingList.picking_items.every(item => item.status === 'picked')
    },
    
    // Totales combinados (picking + storage)
    totalActiveTasksCount() {
      return this.activeTasksCount + this.myStorageLists.filter(list => 
        ['assigned', 'in_progress'].includes(list.status)
      ).length
    },
    totalPendingCount() {
      return this.pendingCount + this.myStorageLists.filter(list => list.status === 'assigned').length
    },
    totalInProgressCount() {
      return this.inProgressCount + this.myStorageLists.filter(list => list.status === 'in_progress').length
    },
    totalCompletedCount() {
      return this.completedCount + this.myStorageLists.filter(list => list.status === 'completed').length
    },
    totalEstimatedTime() {
      const storageTime = this.myStorageLists
        .filter(list => ['assigned', 'in_progress'].includes(list.status))
        .reduce((total, list) => total + (list.estimated_time || 0), 0)
      return this.pickingEstimatedTime + storageTime
    }
  },
  
  async mounted() {
    console.log('Current user in OperatorDashboard:', this.currentUser)
    await Promise.all([
      this.loadMyPickingLists(),
      this.loadMyStorageLists()
    ])
    this.browserMultiFormatReader = new BrowserMultiFormatReader()
  },
  
  watch: {
    currentUser: {
      handler(newUser) {
        // Si el usuario se desloguea, redirigir al login
        if (!newUser) {
          console.log('Usuario deslogueado, redirigiendo al login')
          this.$router.push('/login')
        }
      },
      immediate: true
    }
  },
  
  methods: {
    async loadMyPickingLists() {
      // Verificar que hay un usuario logueado
      if (!this.currentUser?.id) {
        console.log('No hay usuario logueado, saltando carga de picking lists')
        return
      }
      
      this.loadingPicking = true
      try {
        console.log('Loading picking lists for user ID:', this.currentUser.id)
        // Obtener listas asignadas al usuario actual
        const response = await api.getPickingLists({ assigned_user_id: this.currentUser.id })
        console.log('Received picking lists:', response.data)
        this.myPickingLists = response.data
      } catch (error) {
        this.showSnackbar('Error al cargar tareas de picking', 'error')
        console.error('Error loading picking lists:', error)
      } finally {
        this.loadingPicking = false
      }
    },
    
    async startPicking(pickingList) {
      try {
        await api.startPickingList(pickingList.id)
        this.showSnackbar('Picking iniciado exitosamente')
        await this.loadMyPickingLists()
        this.viewPickingDetails(pickingList)
      } catch (error) {
        this.showSnackbar('Error al iniciar picking', 'error')
        console.error('Error starting picking:', error)
      }
    },
    
    async viewPickingDetails(pickingList) {
      try {
        // Obtener detalles completos de la lista
        const response = await api.getPickingList(pickingList.id)
        this.selectedPickingList = response.data
        this.showPickingDialog = true
        
        // Cargar sugerencias de ruta automáticamente
        await this.loadRouteSuggestions()
        
        // Iniciar interval para actualizar progreso cada 5 segundos
        this.startProgressUpdateInterval()
      } catch (error) {
        this.showSnackbar('Error al cargar detalles', 'error')
        console.error('Error loading picking details:', error)
      }
    },
    
    async loadRouteSuggestions() {
      if (!this.selectedPickingList) return
      
      this.loadingRoute = true
      try {
        const response = await api.getRouteSuggestions(this.selectedPickingList.id)
        this.routeSuggestions = response.data
      } catch (error) {
        console.error('Error loading route suggestions:', error)
        // No mostrar error aquí ya que es una carga automática
      } finally {
        this.loadingRoute = false
      }
    },
    
    async pickItem(item) {
      try {
        // Verificar si requiere escaneo y no se ha escaneado
        if (this.selectedPickingList.order?.piece_by_piece_enabled && !item.barcode_scanned) {
          this.showSnackbar('Debe escanear el código de barras antes de completar el picking', 'warning')
          return
        }
        
        // Marcar item como recolectado
        await api.pickItem(item.id, {
          quantity_picked: item.quantity_required,
          status: 'picked'
        })
        
        this.showSnackbar(`Item ${item.product_name} recolectado`)
        
        // Recargar detalles
        await this.viewPickingDetails(this.selectedPickingList)
        await this.loadMyPickingLists()
      } catch (error) {
        if (error.response?.data?.requires_scan) {
          this.showSnackbar('Debe escanear el código de barras antes de completar el picking', 'warning')
        } else {
          this.showSnackbar('Error al recolectar item', 'error')
        }
        console.error('Error picking item:', error)
      }
    },
    
    async completePicking() {
      const listId = this.selectedPickingList.id
      try {
        await api.completePickingList(listId)
        this.showSnackbar('Picking completado exitosamente')
        this.showPickingDialog = false
        await this.loadMyPickingLists()
        // Generar y descargar la guía de despacho operativa (PDF, sin precios)
        await this.downloadDispatchGuide(listId)
      } catch (error) {
        this.showSnackbar('Error al completar picking', 'error')
        console.error('Error completing picking:', error)
      }
    },

    async downloadDispatchGuide(listId) {
      try {
        const response = await api.downloadDispatchGuide(listId)
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `guia_despacho_${listId}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        this.showSnackbar('Guía de despacho generada')
      } catch (error) {
        this.showSnackbar('No se pudo generar la guía de despacho', 'warning')
        console.error('Error generando guía de despacho:', error)
      }
    },
    
    // Utilidades de estado
    getStatusColor(status) {
      const colors = {
        assigned: 'warning',
        in_progress: 'info',
        completed: 'success',
        cancelled: 'error'
      }
      return colors[status] || 'grey'
    },
    
    getStatusText(status) {
      const texts = {
        assigned: 'Asignado',
        in_progress: 'En Progreso',
        completed: 'Completado',
        cancelled: 'Cancelado'
      }
      return texts[status] || status
    },
    
    getPriorityColor(priority) {
      const colors = {
        1: 'error',
        2: 'warning',
        3: 'info',
        4: 'success',
        5: 'grey'
      }
      return colors[priority] || 'grey'
    },
    
    getPriorityText(priority) {
      const texts = {
        1: 'Muy Alta',
        2: 'Alta',
        3: 'Media',
        4: 'Baja',
        5: 'Muy Baja'
      }
      return texts[priority] || `Prioridad ${priority}`
    },
    
    getItemStatusColor(status) {
      const colors = {
        pending: 'warning',
        picked: 'success',
        cancelled: 'error'
      }
      return colors[status] || 'grey'
    },
    
    getItemStatusText(status) {
      const texts = {
        pending: 'Pendiente',
        picked: 'Recolectado',
        cancelled: 'Cancelado'
      }
      return texts[status] || status
    },
    
    formatDateTime(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString('es-CL')
    },
    
    showSnackbar(message, color = 'success') {
      this.snackbar = {
        show: true,
        message,
        color
      }
    },
    
    async optimizeRoute() {
      this.optimizingRoute = true
      try {
        const response = await api.optimizePickingRoute(this.selectedPickingList.id)
        this.routeSuggestions = response.data
        this.showSnackbar('Ruta optimizada exitosamente')
      } catch (error) {
        this.showSnackbar('Error al optimizar ruta', 'error')
        console.error('Error optimizing route:', error)
      } finally {
        this.optimizingRoute = false
      }
    },
    
    async refreshRouteSuggestions() {
      this.loadingRoute = true
      try {
        const response = await api.getRouteSuggestions(this.selectedPickingList.id)
        this.routeSuggestions = response.data
        this.showSnackbar('Ruta actualizada exitosamente')
      } catch (error) {
        this.showSnackbar('Error al actualizar ruta', 'error')
        console.error('Error refreshing route suggestions:', error)
      } finally {
        this.loadingRoute = false
      }
    },
    
    getZoneWeightColor(weight) {
      if (weight < 0.5) return 'success'
      if (weight < 1) return 'info'
      if (weight < 1.5) return 'warning'
      return 'error'
    },
    
    closePickingDialog() {
      this.showPickingDialog = false
      this.routeSuggestions = null
      // Limpiar interval al cerrar el modal
      this.clearProgressUpdateInterval()
    },
    
    async startScanForItem(item) {
      this.currentScanningItem = item
      this.scanError = ''
      this.scanSuccess = ''
      this.manualBarcode = ''
      this.showBarcodeScanner = true
      this.scanTab = 'camera'
      this.cameraAvailable = true
      
      try {
        // Inicializar el lector de códigos de barras
        this.browserMultiFormatReader = new BrowserMultiFormatReader()
        const videoInputDevices = await this.browserMultiFormatReader.listVideoInputDevices()
        
        if (videoInputDevices.length === 0) {
          throw new Error('No se encontraron cámaras disponibles')
        }

        await this.browserMultiFormatReader.decodeFromVideoDevice(
          undefined, // Usar dispositivo por defecto
          this.$refs.videoElement,
          (result) => {
            if (result) {
              this.handleBarcodeScan(result.text)
            }
          }
        )
      } catch (error) {
        console.error('Error initializing barcode scanner:', error)
        
        // Manejar diferentes tipos de errores
        if (error.name === 'NotAllowedError') {
          this.scanError = 'Permisos de cámara denegados. Use la opción manual o permita el acceso a la cámara.'
        } else if (error.name === 'NotFoundError') {
          this.scanError = 'No se encontró ninguna cámara en el dispositivo. Use la opción manual.'
        } else if (error.name === 'NotSupportedError') {
          this.scanError = 'El navegador no soporta acceso a la cámara. Use la opción manual.'
        } else if (error.name === 'NotReadableError') {
          this.scanError = 'La cámara está siendo usada por otra aplicación. Use la opción manual.'
        } else {
          this.scanError = 'Error al acceder a la cámara. Use la opción manual: ' + (error.message || 'Error desconocido')
        }
        
        // Cambiar automáticamente al tab manual si hay error
        this.scanTab = 'manual'
        this.cameraAvailable = false
      }
    },
    
    async handleBarcodeScan(barcode) {
      try {
        // Quitar prefijo AIM del lector (]C1 o +C1 según layout de teclado)
        const response = await api.scanBarcodeForPicking(this.currentScanningItem.id, stripAIM(barcode))
        
        if (response.data.success) {
          this.scanSuccess = response.data.message
          
          // Solo cerrar el escáner si se han escaneado todas las unidades
          if (response.data.all_scanned) {
            this.stopScanner()
            this.showSnackbar(`¡Completado! Todas las unidades de ${this.currentScanningItem.product_name} han sido escaneadas`, 'success')
          } else {
            // Mostrar progreso y mantener el escáner abierto
            this.showSnackbar(`Progreso: ${response.data.quantity_scanned}/${response.data.quantity_required} escaneados`, 'info')
            // Limpiar el campo manual para el siguiente escaneo
            this.manualBarcode = ''
          }
          
          // Recargar detalles del picking
          await this.viewPickingDetails(this.selectedPickingList)
        }
      } catch (error) {
        console.error('Error scanning barcode:', error)
        if (error.response?.data?.error) {
          this.scanError = error.response.data.error
        } else {
          this.scanError = 'Error al validar código de barras'
        }
      }
    },
    
    async stopScanner() {
      try {
        // Detener el lector de códigos de barras
        if (this.browserMultiFormatReader) {
          this.browserMultiFormatReader.reset()
        }
        
        // Detener el stream de video
        if (this.$refs.videoElement && this.$refs.videoElement.srcObject) {
          const stream = this.$refs.videoElement.srcObject
          const tracks = stream.getTracks()
          tracks.forEach(track => track.stop())
          this.$refs.videoElement.srcObject = null
        }
      } catch (error) {
        console.error('Error stopping scanner:', error)
      }
      
      this.showBarcodeScanner = false
      this.currentScanningItem = null
      this.scanError = ''
      this.scanSuccess = ''
      this.manualBarcode = ''
      this.scanTab = 'camera'
      this.cameraAvailable = true
    },
    
    async handleManualBarcodeScan() {
      try {
        // Quitar prefijo AIM del lector (]C1 o +C1 según layout de teclado)
        const response = await api.scanBarcodeForPicking(this.currentScanningItem.id, stripAIM(this.manualBarcode))
        
        if (response.data.success) {
          this.scanSuccess = response.data.message
          
          // Solo cerrar el escáner si se han escaneado todas las unidades
          if (response.data.all_scanned) {
            this.stopScanner()
            this.showSnackbar(`¡Completado! Todas las unidades de ${this.currentScanningItem.product_name} han sido escaneadas`, 'success')
          } else {
            // Mostrar progreso y mantener el escáner abierto
            this.showSnackbar(`Progreso: ${response.data.quantity_scanned}/${response.data.quantity_required} escaneados`, 'info')
            // Limpiar el campo manual para el siguiente escaneo
            this.manualBarcode = ''
          }
          
          // Recargar detalles del picking
          await this.viewPickingDetails(this.selectedPickingList)
        }
      } catch (error) {
        console.error('Error scanning barcode:', error)
        if (error.response?.data?.error) {
          this.scanError = error.response.data.error
        } else {
          this.scanError = 'Error al validar código de barras'
        }
      }
    },
    
    startProgressUpdateInterval() {
      // Limpiar interval existente si existe
      this.clearProgressUpdateInterval()
      
      // Crear nuevo interval que actualice cada 5 segundos
      this.progressUpdateInterval = setInterval(async () => {
        if (this.selectedPickingList && this.showPickingDialog) {
          try {
            // Recargar detalles de la picking list silenciosamente
            const response = await api.getPickingList(this.selectedPickingList.id)
            this.selectedPickingList = response.data
            console.log('Progreso de escaneo actualizado automáticamente')
          } catch (error) {
            console.error('Error actualizando progreso automáticamente:', error)
            // No mostrar error al usuario para no interrumpir la experiencia
          }
        }
      }, 5000) // 5 segundos
    },
    
    clearProgressUpdateInterval() {
      if (this.progressUpdateInterval) {
        clearInterval(this.progressUpdateInterval)
        this.progressUpdateInterval = null
      }
    },
    
    async loadMyStorageLists() {
      // Verificar que hay un usuario logueado
      if (!this.currentUser?.id) {
        console.log('No hay usuario logueado, saltando carga de storage lists')
        return
      }
      
      this.loadingStorage = true
      try {
        console.log('Loading storage lists for user ID:', this.currentUser.id)
        // Obtener listas asignadas al usuario actual
        const response = await api.getStorageLists({ assigned_user_id: this.currentUser.id })
        console.log('Received storage lists:', response.data)
        this.myStorageLists = response.data
      } catch (error) {
        this.showSnackbar('Error al cargar tareas de almacenamiento', 'error')
        console.error('Error loading storage lists:', error)
      } finally {
        this.loadingStorage = false
      }
    },
    
    getStorageStatusColor(status) {
      const colors = {
        assigned: 'warning',
        in_progress: 'info',
        completed: 'success',
        cancelled: 'error'
      }
      return colors[status] || 'grey'
    },
    
    getStorageStatusText(status) {
      const texts = {
        assigned: 'Asignado',
        in_progress: 'En Progreso',
        completed: 'Completado',
        cancelled: 'Cancelado'
      }
      return texts[status] || status
    },
    
    getStorageStrategyText(strategy) {
      const texts = {
        'abc_optimized': 'ABC Optimizado',
        'chaotic_controlled': 'Caótico Controlado',
        'zone_dedicated': 'Zonas Dedicadas',
        'fifo_optimized': 'FIFO Optimizado',
        'space_optimized': 'Espacio Optimizado'
      }
      return texts[strategy] || strategy
    },
    
    getStorageProgress(list) {
      if (list.total_items === 0) return 0
      return (list.completed_items / list.total_items) * 100
    },
    
    async startStorage(storageList) {
      try {
        await api.startStorageList(storageList.id)
        this.showSnackbar('Almacenamiento iniciado exitosamente')
        await this.loadMyStorageLists()
        // Navegar a la vista de ejecución de almacenamiento
        this.$router.push(`/storage-execution/${storageList.id}`)
      } catch (error) {
        this.showSnackbar('Error al iniciar almacenamiento', 'error')
        console.error('Error starting storage:', error)
      }
    },
    
    async continueStorage(storageList) {
      try {
        // Navegar directamente a la vista de ejecución
        this.$router.push(`/storage-execution/${storageList.id}`)
      } catch (error) {
        this.showSnackbar('Error al continuar almacenamiento', 'error')
        console.error('Error continuing storage:', error)
      }
    },
    
    async viewStorageDetails(storageList) {
      try {
        // Navegar a la vista de ejecución para ver detalles
        this.$router.push(`/storage-execution/${storageList.id}`)
      } catch (error) {
        this.showSnackbar('Error al ver detalles de almacenamiento', 'error')
        console.error('Error viewing storage details:', error)
      }
    }
  },
  
  beforeUnmount() {
    this.stopScanner()
    // Limpiar interval al salir de la vista
    this.clearProgressUpdateInterval()
  }
}
</script>

<style scoped>
.operator-dashboard {
  padding: 20px;
}

.operator-header {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
  color: white;
}

.opacity-90 {
  opacity: 0.9;
}

.opacity-75 {
  opacity: 0.75;
}

.stats-card {
  height: 100%;
}

.picking-table {
  background-color: white;
}

.picking-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.picking-table >>> th {
  background-color: #f8f9fa !important;
  font-weight: bold !important;
  border-bottom: 2px solid #dee2e6 !important;
}

.picking-table >>> td {
  border-bottom: 1px solid #dee2e6 !important;
  padding: 12px 16px !important;
}

.picking-table >>> tr:hover {
  background-color: #f8f9fa !important;
}

.items-table {
  background-color: #f8f9fa;
}

.items-table >>> th {
  background-color: #e9ecef !important;
}

.gap-2 {
  gap: 8px;
}

.barcode-scanner {
  width: 100%;
  max-width: 400px;
  height: 300px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #e0e0e0;
}

/* Estilos responsivos */
@media (max-width: 768px) {
  .operator-dashboard {
    padding: 10px;
  }
  
  /* Header del operario responsivo */
  .operator-header .v-card-text {
    padding: 12px;
  }
  
  .operator-name {
    font-size: 1.5rem !important;
    word-break: break-word;
  }
  
  .operator-email {
    font-size: 11px;
    word-break: break-all;
  }
  
  .operator-info {
    max-width: 100%;
    overflow: hidden;
  }
  
  /* Cards de estadísticas más compactas */
  .stats-card .v-card-text {
    padding: 12px;
  }
  
  .stats-card .text-h4 {
    font-size: 1.25rem !important;
  }
  
  .stats-card .text-subtitle-2 {
    font-size: 11px;
  }
  
  /* Pestañas responsivas */
  .v-tab {
    font-size: 12px;
    min-width: auto;
    padding: 8px 12px;
  }
  
  .v-tab .v-icon {
    font-size: 16px;
    margin-right: 4px;
  }
  
  /* Tablas responsivas */
  .v-card-title {
    padding: 12px 16px;
    font-size: 16px;
  }
  
  .v-card-text {
    padding: 12px;
  }
  
  /* Chips más pequeños */
  .v-chip {
    font-size: 10px;
    height: 20px;
  }
  
  /* Botones más pequeños */
  .v-btn--size-small {
    min-width: 32px;
    height: 32px;
    font-size: 11px;
  }
  
  /* Modales responsivos */
  .v-dialog .v-card {
    margin: 8px;
  }
  
  .v-dialog .v-card-title {
    padding: 8px 12px;
    font-size: 14px;
  }
  
  .v-dialog .v-card-text {
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .operator-dashboard {
    padding: 8px;
  }
  
  /* Header para móviles pequeños */
  .operator-header .v-card-text {
    padding: 8px;
  }
  
  .operator-name {
    font-size: 1.25rem !important;
  }
  
  .operator-email {
    font-size: 10px;
  }
  
  .v-avatar {
    width: 40px !important;
    height: 40px !important;
  }
  
  .v-avatar .v-icon {
    font-size: 20px !important;
  }
  
  /* Cards de estadísticas para móviles pequeños */
  .stats-card .v-card-text {
    padding: 8px;
  }
  
  .stats-card .text-h4 {
    font-size: 1rem !important;
  }
  
  .stats-card .text-subtitle-2 {
    font-size: 10px;
  }
  
  .stats-card .v-icon {
    font-size: 24px !important;
  }
  
  /* Pestañas para móviles pequeños */
  .v-tab {
    font-size: 11px;
    padding: 6px 8px;
  }
  
  .v-tab .v-icon {
    font-size: 14px;
    margin-right: 2px;
  }
  
  /* Tablas para móviles pequeños */
  .v-card {
    margin-bottom: 12px;
  }
  
  .v-card-title {
    padding: 8px 12px;
    font-size: 14px;
  }
  
  .v-card-text {
    padding: 8px;
  }
  
  /* Chips más pequeños */
  .v-chip {
    font-size: 9px;
    height: 18px;
  }
  
  /* Botones más compactos */
  .v-btn {
    font-size: 10px;
    min-height: 28px;
  }
  
  .v-btn--size-small {
    min-width: 28px;
    height: 28px;
  }
  
  /* Modales para móviles pequeños */
  .v-dialog .v-card {
    margin: 4px;
    border-radius: 8px;
  }
  
  .v-dialog .v-card-title {
    padding: 6px 8px;
    font-size: 13px;
  }
  
  .v-dialog .v-card-text {
    padding: 8px;
  }
  
  /* Scanner de códigos de barras */
  .barcode-scanner {
    height: 200px;
    max-width: 300px;
  }
}
</style> 